import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaSync, FaUserPlus, FaLink } from "react-icons/fa";
import {
  getAllUsers,
  deleteUser,
  updateUser,
  addUserByAdmin,
  updateUserRole,
} from "../../../services/userServices";
import { replaceUserAdminPermissions } from "../../../services/adminPermissionsServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import useScrollToTop from "../../../hooks/useScollToTop";

import UserModal from "./UserModal";
import { UsersFilters } from "./components/UsersFilters";
import { UsersTable } from "./components/UsersTable";
import { UsersCards } from "./components/UsersCards";
import { ROLES } from "./users.constants";

const GRADIENT =
  "bg-gradient-to-r from-sky-600 via-sky-500 to-cyan-400 text-white";

const roleById = new Map(ROLES.map((r) => [r.id, r]));
const LOGIN_URL = "https://www.monapplimairie.com/login";

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("fr-FR");
  } catch {
    return "—";
  }
}

export default function UsersAdmin() {
  useScrollToTop();
  const { addNotification } = useNotification();

  const [users, setUsers] = useState([]);
  const [viewMode, setViewMode] = useState("table");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const [isModalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      addNotification(
        "Erreur lors de la récupération des utilisateurs.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCopyLoginUrl = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(LOGIN_URL)
        .then(() =>
          addNotification(
            "Lien de connexion copié dans le presse-papier.",
            "success"
          )
        )
        .catch(() =>
          addNotification(
            "Impossible de copier le lien. Vous pouvez le copier manuellement.",
            "error"
          )
        );
    } else {
      addNotification(`Lien de connexion : ${LOGIN_URL}`, "info");
    }
  };

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const out = users.filter((u) => {
      const matchesTerm =
        !term ||
        `${u.nom || ""} ${u.prenom || ""}`.toLowerCase().includes(term) ||
        (u.email || "").toLowerCase().includes(term);

      const matchesRole = selectedRole
        ? String(u.role_id) === String(selectedRole)
        : true;

      return matchesTerm && matchesRole;
    });

    out.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;

      if (sortBy === "date") {
        return (new Date(a.createdOn) - new Date(b.createdOn)) * dir;
      }

      return (
        `${a.nom || ""} ${a.prenom || ""}`.localeCompare(
          `${b.nom || ""} ${b.prenom || ""}`,
          "fr"
        ) * dir
      );
    });

    return out;
  }, [users, searchTerm, selectedRole, sortBy, sortDir]);

  const handleDelete = async (userId) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;

    try {
      await deleteUser(userId);
      addNotification("Utilisateur supprimé.", "success");
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (e) {
      console.error(e);
      addNotification("Erreur lors de la suppression.", "error");
    }
  };

  const openCreate = () => {
    setEditingUser(null);
    setIsCreating(true);
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setIsCreating(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setEditingUser(null);
    setIsCreating(false);
    setModalOpen(false);
  };

  const handleSubmitUser = async (payload) => {
    const { adminPermissions = [], ...userPayload } = payload;

    try {
      let permissionSaveFailed = false;

      if (isCreating) {
        const created = await addUserByAdmin(userPayload);
        const newUser = created?.user || created;

        if (!newUser?.id) {
          throw new Error("Utilisateur non créé");
        }

        try {
          await replaceUserAdminPermissions(
            newUser.id,
            Number(userPayload.role_id) === 1 ? [] : adminPermissions
          );
        } catch (permissionError) {
          permissionSaveFailed = true;
          console.error(
            "Erreur lors de la sauvegarde des permissions :",
            permissionError
          );
        }

        setUsers((prev) => [newUser, ...prev]);

        if (permissionSaveFailed) {
          addNotification(
            "Utilisateur créé, mais les permissions n'ont pas pu être enregistrées.",
            "error"
          );
        } else {
          addNotification(
            "Utilisateur créé. Pensez à le prévenir qu’il peut se connecter via le lien de connexion et utiliser « Mot de passe oublié ».",
            "success"
          );
        }
      } else if (editingUser) {
        await updateUser(editingUser.id, userPayload);

        try {
          await replaceUserAdminPermissions(
            editingUser.id,
            Number(userPayload.role_id) === 1 ? [] : adminPermissions
          );
        } catch (permissionError) {
          permissionSaveFailed = true;
          console.error(
            "Erreur lors de la sauvegarde des permissions :",
            permissionError
          );
        }

        setUsers((prev) =>
          prev.map((u) =>
            u.id === editingUser.id ? { ...u, ...userPayload } : u
          )
        );

        if (permissionSaveFailed) {
          addNotification(
            "Utilisateur mis à jour, mais les permissions n'ont pas pu être enregistrées.",
            "error"
          );
        } else {
          addNotification("Utilisateur mis à jour.", "success");
        }
      }

      closeModal();
    } catch (e) {
      console.error(e);
      addNotification(
        "Erreur lors de l’enregistrement de l’utilisateur.",
        "error"
      );
    }
  };

  const handleInlineRole = async (user, newRoleId) => {
    const old = user.role_id;

    setUsers((prev) =>
      prev.map((u) => (u.id === user.id ? { ...u, role_id: newRoleId } : u))
    );

    try {
      await updateUserRole(user.id, newRoleId);

      if (Number(newRoleId) === 1) {
        try {
          await replaceUserAdminPermissions(user.id, []);
        } catch (permissionError) {
          console.error(
            "Erreur lors du nettoyage des permissions admin :",
            permissionError
          );
        }
      }

      addNotification("Rôle mis à jour.", "success");
    } catch (e) {
      console.error(e);

      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, role_id: old } : u))
      );

      addNotification("Échec de mise à jour du rôle.", "error");
    }
  };

  const kpis = useMemo(() => {
    const total = filtered.length;
    const byRole = ROLES.reduce((acc, r) => ({ ...acc, [r.id]: 0 }), {});

    for (const u of filtered) {
      if (byRole[u.role_id] !== undefined) byRole[u.role_id]++;
    }

    return { total, byRole };
  }, [filtered]);

  return (
    <div className="p-5 md:p-8 mt-[100px] space-y-6">
      <div className={`rounded-2xl p-6 shadow-sm ${GRADIENT}`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">
              Utilisateurs de l’application
            </h1>
            <p className="text-sm opacity-90 mt-1">
              Gestion des comptes administrateurs et utilisateurs. Vous pouvez
              créer des comptes, définir leur rôle et limiter l’accès à
              certaines pages du tableau de bord administratif.
            </p>

            <div className="mt-3 px-3 py-2 rounded-xl bg-white/15 text-xs sm:text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <span className="font-semibold">Important :</span>{" "}
                après la création d’un nouvel utilisateur, pensez à le prévenir
                qu’il est enregistré sur ce site et qu’il peut se connecter sur{" "}
                <span className="underline">/login</span> puis cliquer sur{" "}
                <span className="font-semibold">« Mot de passe oublié »</span>{" "}
                pour définir son mot de passe.
              </div>
              <button
                type="button"
                onClick={handleCopyLoginUrl}
                className="inline-flex items-center justify-center px-3 py-1.5 rounded-lg bg-white/90 text-sky-700 hover:bg-slate-100 text-xs sm:text-sm shadow-sm"
              >
                <FaLink className="mr-1.5" />
                Copier le lien de connexion
              </button>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={fetchUsers}
              className="bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-xl inline-flex items-center gap-2"
            >
              <FaSync /> Rafraîchir
            </button>
            <button
              onClick={openCreate}
              className="bg-white text-sky-700 hover:bg-slate-100 px-4 py-2 rounded-xl inline-flex items-center gap-2 shadow-sm"
            >
              <FaUserPlus /> Nouvel utilisateur / invitation
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
          <KpiCard label="Total" value={kpis.total} />
          <KpiCard label="Administrateurs" value={kpis.byRole[1] || 0} />
          <KpiCard label="Utilisateurs" value={kpis.byRole[2] || 0} />
        </div>
      </div>

      <div className="rounded-2xl p-4 bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
        <UsersFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          roles={ROLES}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDir={sortDir}
          setSortDir={setSortDir}
          viewMode={viewMode}
          setViewMode={setViewMode}
          totalCount={filtered.length}
        />
      </div>

      <div className="rounded-2xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
        {loading ? (
          <SkeletonTable />
        ) : filtered.length === 0 ? (
          <EmptyList />
        ) : viewMode === "table" ? (
          <UsersTable
            data={filtered}
            onEdit={openEdit}
            onDelete={handleDelete}
            onInlineRole={handleInlineRole}
            roleById={roleById}
            fmtDate={fmtDate}
            roles={ROLES}
          />
        ) : (
          <UsersCards
            data={filtered}
            onEdit={openEdit}
            onDelete={handleDelete}
            onInlineRole={handleInlineRole}
            roleById={roleById}
            fmtDate={fmtDate}
            roles={ROLES}
          />
        )}
      </div>

      {isModalOpen && (
        <UserModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={handleSubmitUser}
          user={editingUser}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl p-4 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 shadow-sm">
      <div className="text-xs text-slate-600 dark:text-slate-400">{label}</div>
      <div className="text-2xl font-semibold mt-1 text-slate-900 dark:text-slate-50 tabular-nums">
        {value}
      </div>
      {hint ? (
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-14 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse"
        />
      ))}
    </div>
  );
}

function EmptyList() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
        📭
      </div>
      <div className="font-medium">
        Aucun utilisateur ne correspond à vos filtres.
      </div>
      <div className="text-sm text-slate-500 mt-1">
        Essayez d’élargir votre recherche.
      </div>
    </div>
  );
}